import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  TrendingUp, Clock, AlertCircle, Scale,
  CalendarDays, ArrowUpRight, ArrowDownRight, FileText
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

function money(n) {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
}
function mesNombre(periodo) {
  if (!periodo) return "";
  const [y, m] = periodo.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return meses[parseInt(m) - 1] + " " + y.slice(2);
}

const ESTADO_COLORS = {
  emitida: { bg: "#fff8e1", color: "#e65100" },
  pagada:  { bg: "#e8f5e9", color: "#2e7d32" },
  anulada: { bg: "#fce4ec", color: "#c62828" },
  vencida: { bg: "#fbe9e7", color: "#bf360c" },
};

/* ── KPI Card ── */
function KpiCard({ icon: Icon, label, value, sub, color, trend, trendLabel }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "22px 24px", boxShadow: "0 1px 6px #0000000a", flex: 1, minWidth: 180 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={18} color={color} />
        </div>
        {trend !== undefined && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: trend >= 0 ? "#10b981" : "#ef4444" }}>
            {trend >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#0d1117", marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>}
      {trendLabel && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{trendLabel}</div>}
    </div>
  );
}

/* ── Tooltip personalizado ── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", boxShadow: "0 4px 12px #00000015", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0d1117", marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ fontSize: 12, color: p.color, fontWeight: 600 }}>
          {p.name}: {money(p.value)}
        </div>
      ))}
    </div>
  );
}

/* ── Calendario F29 ── */
function CalendarioF29({ periodos }) {
  const hoy = new Date();
  const items = periodos.slice(0, 4).map(p => {
    const [y, m] = p.split("-");
    const vence = new Date(parseInt(y), parseInt(m), 20);
    const dias = Math.ceil((vence - hoy) / (1000 * 60 * 60 * 24));
    return { periodo: p, vence, dias, label: mesNombre(p) };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map(({ periodo, dias, label, vence }) => {
        const urgente = dias >= 0 && dias <= 5;
        const vencido = dias < 0;
        const color = vencido ? "#ef4444" : urgente ? "#f59e0b" : "#10b981";
        const bg = vencido ? "#fef2f2" : urgente ? "#fffbeb" : "#f0fdf4";
        return (
          <div key={periodo} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: bg, borderRadius: 10, border: `1px solid ${color}30` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>F29 · {label}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Vence {fmtDate(vence.toISOString().split("T")[0])}</div>
            </div>
            <div style={{ background: color + "20", color, borderRadius: 8, padding: "4px 12px", fontSize: 11, fontWeight: 800 }}>
              {vencido ? `Vencido ${Math.abs(dias)}d` : dias === 0 ? "¡Hoy!" : `${dias} días`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    ingresosMes: 0, ingresosAnterior: 0,
    facturasPendientes: 0, montoPendiente: 0,
    totalIngresos: 0, totalGastos: 0,
    proximoF29: null, diasF29: null,
    grafico: [], ultimasFacturas: [], periodos: [],
  });

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setLoading(true);
    const hoy = new Date();
    const periodoActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
    const mesAnterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const periodoAnterior = `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, "0")}`;

    // Ingresos mes actual
    const { data: ingresosMesData } = await supabase
      .from("ingresos").select("monto_neto, monto_total")
      .eq("periodo", periodoActual).neq("estado", "anulada");

    // Ingresos mes anterior
    const { data: ingresosAntData } = await supabase
      .from("ingresos").select("monto_neto")
      .eq("periodo", periodoAnterior).neq("estado", "anulada");

    // Facturas pendientes
    const { data: pendientesData } = await supabase
      .from("ingresos").select("id, monto_total")
      .eq("estado", "emitida");

    // Total ingresos
    const { data: todosIngresos } = await supabase
      .from("ingresos").select("monto_neto").neq("estado", "anulada");

    // Gastos (tabla gastos si existe)
    const { data: todosGastos } = await supabase
      .from("gastos").select("monto_neto").neq("estado", "anulado");

    // Últimas 6 facturas
    const { data: ultimas } = await supabase
      .from("ingresos").select("*")
      .order("created_at", { ascending: false }).limit(6);

    // Gráfico por período
    const { data: periodoData } = await supabase
      .from("ingresos").select("periodo, monto_neto, estado")
      .neq("estado", "anulada").order("periodo");

    // Periodos para F29
    const { data: periodosData } = await supabase
      .from("ingresos").select("periodo").order("periodo", { ascending: false });
    const periodosUnicos = [...new Set((periodosData || []).map(p => p.periodo).filter(Boolean))];

    // Calcular próximo F29
    const diaActual = hoy.getDate();
    let proximoPeriodo = periodoActual;
    let diasF29;
    const venceEsteMes = new Date(hoy.getFullYear(), hoy.getMonth(), 20);
    if (diaActual > 20) {
      const sig = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 20);
      proximoPeriodo = `${sig.getFullYear()}-${String(sig.getMonth() + 1).padStart(2, "0")}`;
      diasF29 = Math.ceil((sig - hoy) / (1000 * 60 * 60 * 24));
    } else {
      diasF29 = Math.ceil((venceEsteMes - hoy) / (1000 * 60 * 60 * 24));
    }

    // Agrupar gráfico
    const graficoMap = {};
    (periodoData || []).forEach(r => {
      if (!graficoMap[r.periodo]) graficoMap[r.periodo] = 0;
      graficoMap[r.periodo] += r.monto_neto || 0;
    });
    const grafico = Object.entries(graficoMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([periodo, total]) => ({ periodo, label: mesNombre(periodo), total }));

    const ingresosMes = (ingresosMesData || []).reduce((s, r) => s + (r.monto_neto || 0), 0);
    const ingresosAnt = (ingresosAntData || []).reduce((s, r) => s + (r.monto_neto || 0), 0);
    const trend = ingresosAnt > 0 ? Math.round(((ingresosMes - ingresosAnt) / ingresosAnt) * 100) : 0;

    setData({
      ingresosMes,
      ingresosAnterior: ingresosAnt,
      trend,
      facturasPendientes: (pendientesData || []).length,
      montoPendiente: (pendientesData || []).reduce((s, r) => s + (r.monto_total || 0), 0),
      totalIngresos: (todosIngresos || []).reduce((s, r) => s + (r.monto_neto || 0), 0),
      totalGastos: (todosGastos || []).reduce((s, r) => s + (r.monto_neto || 0), 0),
      proximoPeriodo, diasF29,
      grafico, ultimasFacturas: ultimas || [],
      periodos: periodosUnicos,
    });
    setLoading(false);
  }

  const balance = data.totalIngresos - data.totalGastos;
  const mesActual = new Date().toLocaleDateString("es-CL", { month: "long", year: "numeric" });

  if (loading) return (
    <div style={{ padding: 40, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontFamily: "'DM Sans', sans-serif" }}>
      Cargando dashboard...
    </div>
  );

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0d1117", margin: 0 }}>Dashboard</h1>
        <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 0", textTransform: "capitalize" }}>{mesActual} · TuAplicacion SpA</p>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <KpiCard
          icon={TrendingUp} label="Ingresos del mes" color="#29abe2"
          value={money(data.ingresosMes)}
          trend={data.trend}
          trendLabel={`vs ${money(data.ingresosAnterior)} mes anterior`}
        />
        <KpiCard
          icon={Clock} label="Facturas pendientes" color="#f59e0b"
          value={data.facturasPendientes}
          sub={`${money(data.montoPendiente)} por cobrar`}
        />
        <KpiCard
          icon={Scale} label="Balance total" color={balance >= 0 ? "#10b981" : "#ef4444"}
          value={money(balance)}
          sub={`Ingresos: ${money(data.totalIngresos)}`}
        />
        <KpiCard
          icon={AlertCircle} label="Próximo F29" color={data.diasF29 <= 5 ? "#ef4444" : "#8b5cf6"}
          value={data.diasF29 <= 0 ? "¡Vencido!" : `${data.diasF29} días`}
          sub={`Período ${data.proximoPeriodo}`}
        />
      </div>

      {/* Gráfico + F29 */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        {/* Gráfico ingresos */}
        <div style={{ flex: 1, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 6px #0000000a" }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>Ingresos por mes</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Últimos 6 meses · Monto neto CLP</div>
          </div>
          {data.grafico.length === 0 ? (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>Sin datos suficientes</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.grafico} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="total" name="Ingresos" radius={[6, 6, 0, 0]}>
                  {data.grafico.map((entry, i) => (
                    <Cell key={i} fill={i === data.grafico.length - 1 ? "#29abe2" : "#29abe240"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Calendario F29 */}
        <div style={{ width: 300, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 6px #0000000a" }}>
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarDays size={16} color="#8b5cf6" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>Vencimientos F29</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Día 20 de cada mes</div>
            </div>
          </div>
          {data.periodos.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: 20 }}>Sin períodos registrados</div>
          ) : (
            <CalendarioF29 periodos={data.periodos} />
          )}
        </div>
      </div>

      {/* Últimas facturas */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8 }}>
          <FileText size={15} color="#29abe2" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>Últimas facturas</span>
        </div>
        {data.ultimasFacturas.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Sin facturas registradas</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["Folio", "Cliente", "Fecha", "Total", "Estado"].map(h => (
                  <th key={h} style={{ padding: "10px 20px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.ultimasFacturas.map(f => {
                const s = ESTADO_COLORS[f.estado] || ESTADO_COLORS.emitida;
                return (
                  <tr key={f.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                    <td style={{ padding: "12px 20px", fontWeight: 700, color: "#29abe2" }}>{f.folio || "—"}</td>
                    <td style={{ padding: "12px 20px", color: "#0d1117", fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.razon_cliente || "—"}</td>
                    <td style={{ padding: "12px 20px", color: "#64748b" }}>{fmtDate(f.fecha_emision)}</td>
                    <td style={{ padding: "12px 20px", fontWeight: 800, color: "#0d1117" }}>{money(f.monto_total)}</td>
                    <td style={{ padding: "12px 20px" }}>
                      <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
                        {f.estado}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
    </div>
  );
}